import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import Modal from "../../../../../../components/common/Modal";
import PrimaryButton from "../../../../../../components/common/PrimaryButton";
import OutlinedButton from "../../../../../../components/common/OutlinedButton";
import TextInput, {
  InputType,
} from "../../../../../../components/forms/textInput";

interface IOtpLimitPromptModalProps {
  open: boolean;
  currentValue: number | null | undefined;
  /** Translation keys; default to the otp-send wording. */
  titleKey?: string;
  subtitleKey?: string;
  handleClose: () => void;
  // eslint-disable-next-line no-unused-vars
  handleConfirm: (otpSendHourlyLimit: number | null, reason: string) => void;
}

/**
 * Collects a required reason before changing (or clearing) a client's otp-send-hourly-limit
 * override (design doc §6.5) — same discipline as ReasonPromptModal's auto-accept-consent flow
 * (§6.2), extended with the numeric value itself since this isn't a fixed-target toggle. An empty
 * value clears the override, reverting the client to the global default.
 */
const OtpLimitPromptModal = ({
  open,
  currentValue,
  titleKey = "lbl_edit_otp_send_hourly_limit_title",
  subtitleKey = "lbl_otp_send_hourly_limit_subtitle",
  handleClose,
  handleConfirm,
}: IOtpLimitPromptModalProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setValue(currentValue != null ? String(currentValue) : "");
    } else {
      setValue("");
      setReason("");
    }
  }, [open, currentValue]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    handleConfirm(trimmed === "" ? null : Number(trimmed), reason.trim());
  };

  return (
    <Modal
      visible={open}
      onCancel={handleClose}
      customModalClassName={styles.modal}
      customModalContainerClassName={styles.modalContainer}>
      <div className={styles.container}>
        <h1 className={styles.title}>{t(titleKey)}</h1>
        <p className={styles.subtitle}>{t(subtitleKey)}</p>

        <TextInput
          type={InputType.NUMBER}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("lbl_otp_send_hourly_limit_placeholder")}
        />

        <TextInput
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("lbl_reason_prompt_placeholder")}
        />

        <div className={styles.actions}>
          <PrimaryButton disabled={!reason.trim()} onClick={handleSubmit}>
            {t("lbl_confirm")}
          </PrimaryButton>
          <OutlinedButton onClick={handleClose}>
            {t("lbl_cancel")}
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default OtpLimitPromptModal;
